import React, { useCallback, useContext, useRef } from "react";
import { CONTENT_TYPE } from "src/utils/asinUtils";
import { BookState, NativeAPI } from "src/utils/deviceUtils";
import Button from "../Button/Button";
import AcquisitionManagerContext, { FailableResult, onSuccess } from "src/contexts/AcquisitionManagerContext";
import debug from "src/utils/debugUtils";
import TranslationsContext from "src/contexts/TranslationsContext";
import Spinner from "../Spinner/Spinner";
import styles from "./BookBuyButton.module.scss";
import { MetricsContext, newMetricsWithContext, recordMetricOnce } from "src/utils/metricsUtils";
import { hasAudio } from "src/utils/programLogoUtils";
import focusHelper from "src/utils/focusHelper";
import DeviceContext from "src/contexts/DeviceContext";
import useImpressionCallback from "src/utils/useImpressionCallback";
import { getBuyingOptionWithReadNowCta, getBorrowBuyingOption } from "src/utils/aapiResponseUtils";
import { programCodeFromSticker } from "src/utils/programLogoUtils";

type PropTypes = {
    item: QuickViewAsinMetadata;
    coverImageRef?: React.RefObject<HTMLDivElement>;
    isMiniHeader?: boolean;
    updateDownloadState: (contentType: CONTENT_TYPE, shouldRetryOnUnknown?: boolean) => void;
    redeemOfferByActionType: (actionType: string, callback: VoidFunction) => Promise<FailableResult>;
    hasFullBook: boolean;
    canAddToLibraryForFree: boolean;
    primaryCtaAlreadyShown: boolean;
    bookStatus: BookStatus;
    sampleOwned: boolean;
    companionOwned: boolean;
    bookActionInProgress: boolean;
};

type CtaButtonProps = {
    buttonBehavior: ButtonBehavior;
    bookStatus: BookStatus;
}

type ButtonBehavior = {
    label: string;
    labelColor: string;
    backgroundColor: string;
    showSpinner?: boolean;
    action: VoidFunction;
    metricsContext?: MetricsContext;
    impressionMetricName?: string;
};

const noOp = () => { /* NoOp */ };

const BookBuyButton: React.FC<PropTypes> = ({ item, coverImageRef, isMiniHeader, bookStatus, sampleOwned, companionOwned, bookActionInProgress, hasFullBook, canAddToLibraryForFree, primaryCtaAlreadyShown, updateDownloadState, redeemOfferByActionType }: PropTypes) => {
    const context = useContext(DeviceContext);
    const translations = useContext(TranslationsContext);
    const acquisitionManagerContext = useContext(AcquisitionManagerContext);
    const metricsContext = { namespace: isMiniHeader ? "BookBuyButton.miniHeader" : "BookBuyButton", qv_asin: item.asin }
    const metrics = newMetricsWithContext(metricsContext.namespace, metricsContext.qv_asin);
    // TODO: This should come from the CallToAction resource in AAPI instead of the sticker code
    const withAudio = !isMiniHeader && hasAudio(item.kindleProgram);
    const a11yTarget = useRef<HTMLButtonElement>(null);
    const isOpening = acquisitionManagerContext.isOpening(item.asin);
    const isTeenAccount = context.isTeenAccount;
    const isBorrowed = acquisitionManagerContext.isBorrowed(item.asin);

    const requestA11yFocus = useCallback(() => {
        focusHelper.requestFocus(a11yTarget.current);
    }, [a11yTarget]);

    // TODO: This is not my favorite
    const primaryCtaButtonTextColor = primaryCtaAlreadyShown === false
        ? styles.buttonTextColorCTA
        : styles.buttonTextColor;

    const primaryCtaButtonBackgroundColor = primaryCtaAlreadyShown === false
        ? styles.buttonBackgroundColorCTA
        : styles.buttonBackgroundColor;

    const getCoverImagePosition = () => {
        const coverImageRect = coverImageRef?.current?.getBoundingClientRect();
        if (coverImageRect) {
            return {
                x: coverImageRect.x,
                y: coverImageRect.y,
                width: coverImageRect.width,
                height: coverImageRect.height,
            };
        }
    };

    const openBook = () => {
        const contentType = item.isMagazine
            ? CONTENT_TYPE.MAGZ
            : hasFullBook
                ? CONTENT_TYPE.EBOK
                : CONTENT_TYPE.EBSP;
        metrics.recordBehavioralMetric(`ReadNow.${contentType}.click`, 1);
        if (contentType === CONTENT_TYPE.EBSP) {
            updateDownloadState(contentType);
        }
        acquisitionManagerContext.openBook({
             asin: item.asin,
             withAudioWhenBorrowedFrom: hasAudio(item.kindleProgram)
                 ? programCodeFromSticker(item.kindleProgram?.stickerStyleCode)
                 : undefined,
             title: item.title,
             authors: item.authors,
             type: contentType,
             coverImagePosition: getCoverImagePosition()
         });
    };

    const getReadNowCtaLabel = (): string => {
        if (context.enableAapiCta) {
            // we know there's a buying option with a read now CTA because we only render the read now behavior
            // if we have the full book (which is only true if we have a BO with a read now CTA).
            const buyingOptionsReadNowText = getBuyingOptionWithReadNowCta(item.allBuyingOptions)?.callToAction?.entity?.readNow?.data?.displayString;
            if (buyingOptionsReadNowText) {
                return buyingOptionsReadNowText;
            }
        }
        const hasCompanion = companionOwned || (isBorrowed && withAudio);
        return hasCompanion ? translations.getText("read-and-listen-now") : translations.getText("read-now");
    }

    const readNowBehavior: ButtonBehavior = {
        label: getReadNowCtaLabel(),
        labelColor: styles.buttonTextColorReadNow,
        backgroundColor: styles.buttonBackgroundColorReadNow,
        showSpinner: isOpening,
        action: openBook,
        metricsContext: metricsContext,
        impressionMetricName: `ReadNow.${item.isMagazine ? CONTENT_TYPE.MAGZ : CONTENT_TYPE.EBOK}.Render`
    };

    const readSampleBehavior: ButtonBehavior = {
        label: translations.getText("read-sample"),
        labelColor: styles.buttonTextColorReadNow,
        backgroundColor: styles.buttonBackgroundColorReadNow,
        showSpinner: isOpening,
        action: openBook,
        metricsContext: metricsContext,
        impressionMetricName: "ReadSample.Render" 
    };

    const getReadForFreeCta = (): string => {
        if (context.enableAapiCta) {
            const borrowBuyingOption = getBorrowBuyingOption(item.allBuyingOptions);
            if (borrowBuyingOption?.type === "WAIT_FOR_FREE") {
                return borrowBuyingOption?.callToAction?.entity?.readNow?.data?.displayString || translations.getText("read-for-free");
            }
        }
        return translations.getText("read-for-free");
    }
    
    const readForFreeBehavior: ButtonBehavior = {
        label: withAudio
            ? translations.getText("read-and-listen-for-free")
            : getReadForFreeCta(),
        labelColor: primaryCtaButtonTextColor,
        backgroundColor: primaryCtaButtonBackgroundColor,
        action: () => {
            metrics.recordBehavioralMetric("ReadForFree.click", 1);
            isTeenAccount
                ? NativeAPI.openTeenAccountError(item)
                : redeemOfferByActionType("Borrow", requestA11yFocus);
        },
        metricsContext: metricsContext,
        impressionMetricName: "ReadForFree.Render"
    };

    const amazonFirstReadsBehavior: ButtonBehavior = {
        label: item?.primaryPurchaseBuyingOption?.callToAction?.entity?.oneClick?.data?.displayString ?? "",
        labelColor: primaryCtaButtonTextColor,
        backgroundColor: primaryCtaButtonBackgroundColor,
        action: () => {
            metrics.recordBehavioralMetric("AmazonFirstReads.click", 1);
            isTeenAccount
                ? NativeAPI.openTeenAccountError(item)
                : redeemOfferByActionType("AFR", requestA11yFocus);
        },
        metricsContext: metricsContext,
        impressionMetricName: "AmazonFirstReads.Render"
    };

    const actionInProgressBehavior: ButtonBehavior = {
        label: translations.getText("processing"),
        showSpinner: true,
        labelColor: primaryCtaButtonTextColor,
        backgroundColor: primaryCtaButtonBackgroundColor,
        action: noOp,
    };

    const getAddToLibraryForFreeCta =  () : string => {
        return (context.enableAapiCta ?
                item.primaryPurchaseBuyingOption?.callToAction?.entity?.oneClick?.data?.displayString
                : '')
                || translations.getText("add-to-your-library-for-free");
    }

    const addToLibraryForFreeBehavior: ButtonBehavior = {
        label: getAddToLibraryForFreeCta(),
        labelColor: styles.buttonTextColorCTA,
        backgroundColor: styles.buttonBackgroundColorCTA,
        action: () => {
            metrics.recordBehavioralMetric("AddToLibraryForFree.click", 1);
            isTeenAccount
                ? NativeAPI.openTeenAccountError(item)
                : redeemOfferByActionType("Buy", requestA11yFocus);
        },
        metricsContext: metricsContext,
        impressionMetricName: "AddToLibraryForFree.Render"
    };

    const downloadInProgressBehavior: ButtonBehavior = {
        label: translations.formatText("download-progress", { percentage: Math.ceil(bookStatus.downloadProgress * 100) }),
        labelColor: primaryCtaButtonTextColor,
        backgroundColor: primaryCtaButtonBackgroundColor,
        action: noOp,
    };

    const downloadAsin = (type: CONTENT_TYPE) => {
        debug.log(`Downloading: ${item.asin} ${type} ${item.title}`);
        NativeAPI.downloadBook(item.asin, type, item.title)
            ?.catch(debug.error)
            .finally(() => updateDownloadState(type, true));
    };

    const downloadSampleBehavior: ButtonBehavior = {
        label: translations.getText("download-sample"),
        labelColor: primaryCtaButtonTextColor,
        backgroundColor: primaryCtaButtonBackgroundColor,
        action: () => {
            metrics.recordBehavioralMetric("DownloadSample.click", 1);
            redeemOfferByActionType("Sample", requestA11yFocus).then(onSuccess(() => downloadAsin(CONTENT_TYPE.EBSP)));
        },
        metricsContext: metricsContext,
        impressionMetricName: "DownloadSample.Render"
    };

    const getButtonBehavior = (): ButtonBehavior | null => {
        if (hasFullBook) {
            return readNowBehavior;
        }
        if (!item.hasAskConsentOption || context.bypassAskConsent) {
            if (context.enableAfrAcquisitions && item.hasFreeAfrOption) {
                return bookActionInProgress ? actionInProgressBehavior : amazonFirstReadsBehavior;
            }
            if (item.canBorrow) {
                return bookActionInProgress ? actionInProgressBehavior : readForFreeBehavior;
            }
            if (canAddToLibraryForFree) {
                return bookActionInProgress ? actionInProgressBehavior : addToLibraryForFreeBehavior;
            }
        }
        if (sampleOwned) {
            return readSampleBehavior;
        }
        if (!item.hasSample) {
            return null;
        }
        if (bookActionInProgress) {
            return downloadInProgressBehavior;
        }
        switch (bookStatus.bookState) {
            case BookState.AZPluginBookStateInLibraryDownloading:
                return downloadInProgressBehavior;
            case BookState.AZPluginBookStateInLibraryOnDevice:
                return readSampleBehavior;
            default:
                return downloadSampleBehavior;
        }
    };

    const buttonBehavior = getButtonBehavior();

    if (!buttonBehavior) {
        return null;
    }

    return (
        <CtaButton buttonBehavior={buttonBehavior} bookStatus={bookStatus} />
    );
};

const CtaButton: React.FC<CtaButtonProps> = ({ buttonBehavior, bookStatus }) => {
    const buttonRef = useRef<HTMLButtonElement>(null);  
    useImpressionCallback(buttonRef, () => {
        if (buttonBehavior.metricsContext && buttonBehavior.impressionMetricName) {
            recordMetricOnce(buttonBehavior.metricsContext, buttonBehavior.impressionMetricName, 1);
        }
    });

    return (
        <Button
            onClick={buttonBehavior.action}
            showProgressBar={bookStatus.bookState === BookState.AZPluginBookStateInLibraryDownloading}
            progressPercentage={bookStatus.downloadProgress * 100}
            backgroundColor={buttonBehavior.backgroundColor}
            ref={buttonRef}
        >
            { buttonBehavior.showSpinner === true
                ? (<span> <Spinner size="min(1rem, 28px)" /> </span>)
                : (<span style={{ color: buttonBehavior.labelColor }}>{buttonBehavior.label}</span>)
            }
        </Button>
    )
}

export default BookBuyButton;
